import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '@/services/api';
import SearchBar from '@/components/SearchBar';
import FilterDropdown from '@/components/FilterDropdown';
import RestaurantModal from '@/components/RestaurantModal';
import { Can } from '@/components/auth/Can';
import { PERM } from '@/constants/permissions';
import type { Restaurant } from '@/types/api';
import { Plus, Edit2, Trash2, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 24;
const SEARCH_DEBOUNCE_MS = 300;

export default function RestaurantsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [editingRestaurant, setEditingRestaurant] = useState<Restaurant | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Debounce free-text search so every keystroke doesn't trigger a fetch.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // A filter change can invalidate the current page (e.g. page 3 of an
  // unfiltered list may not exist once a search narrows the result set).
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter, stateFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['restaurants', page, debouncedSearch, statusFilter, stateFilter],
    queryFn: () =>
      apiClient.getRestaurants(page, PAGE_SIZE, {
        search: debouncedSearch || undefined,
        active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        state: stateFilter === 'all' ? undefined : stateFilter,
      }),
  });

  // Independent of the current filters/page, so the dropdown always lists every
  // county visible to the caller rather than just whatever's on the current page.
  const { data: availableStates } = useQuery({
    queryKey: ['restaurant-states'],
    queryFn: () => apiClient.getRestaurantStates(),
    staleTime: 5 * 60 * 1000,
  });

  const restaurants = data?.content ?? [];
  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || stateFilter !== 'all';

  const openCreateModal = () => {
    setEditingRestaurant(null);
    setIsModalOpen(true);
  };

  const openEditModal = (restaurant: Restaurant) => {
    setEditingRestaurant(restaurant);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setEditingRestaurant(null);
    setIsModalOpen(false);
  };

  const deleteMutation = useMutation({
    mutationFn: (restaurantId: number) => apiClient.deleteRestaurant(restaurantId),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['restaurants'] });
    },
    onError: (error: any) => {
      setDeleteError(error.response?.data?.message || 'Failed to delete restaurant');
    },
  });

  const handleDelete = async (restaurantId: number) => {
    if (
      window.confirm(
        'Are you sure you want to delete this restaurant? This action cannot be undone.'
      )
    ) {
      try {
        await deleteMutation.mutateAsync(restaurantId);
      } catch {
        // surfaced via deleteMutation's onError -> deleteError banner
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-center">
        <AlertTriangle className="h-8 w-8 text-red-500" />
        <div className="text-gray-900 font-medium">Failed to load restaurants</div>
        <button onClick={() => refetch()} className="btn btn-outline h-10">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Restaurants</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage all restaurant locations
          </p>
        </div>
        <Can name={PERM.RESTAURANT_CREATE}>
          <button onClick={openCreateModal} className="btn btn-primary h-10">
            <Plus className="h-4 w-4 mr-2" />
            Add Restaurant
          </button>
        </Can>
      </div>

      {deleteError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center justify-between">
          <span>{deleteError}</span>
          <button onClick={() => setDeleteError(null)} className="text-red-700 hover:text-red-900 font-medium ml-4">
            Dismiss
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by name, city, or address..."
            />
          </div>
          <FilterDropdown
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { label: 'All Restaurants', value: 'all' },
              { label: 'Active Only', value: 'active' },
              { label: 'Inactive Only', value: 'inactive' },
            ]}
          />
          <FilterDropdown
            label="County"
            value={stateFilter}
            onChange={setStateFilter}
            options={[
              { label: 'All Counties', value: 'all' },
              ...(availableStates ?? []).map((state) => ({
                label: state,
                value: state,
              })),
            ]}
          />
        </div>

        {/* Results count */}
        <div className="mt-3 text-sm text-gray-600">
          Showing {restaurants.length} of {data?.totalElements ?? 0} restaurants
          {(data?.totalPages ?? 0) > 1 && ` (page ${page + 1} of ${data?.totalPages})`}
        </div>
      </div>

      {/* Restaurants Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {restaurants.map((restaurant) => (
          <div key={restaurant.id} className="card p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {restaurant.name}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {restaurant.city}, {restaurant.state}
                </p>
              </div>
              <div className="flex items-center space-x-1">
                <span
                  className={`badge ${
                    restaurant.active ? 'badge-success' : 'badge-gray'
                  }`}
                >
                  {restaurant.active ? 'Active' : 'Inactive'}
                </span>
                <Can name={PERM.RESTAURANT_UPDATE}>
                  <button
                    onClick={() => openEditModal(restaurant)}
                    className="text-primary-600 hover:text-primary-900 hover:bg-primary-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    aria-label={`Edit ${restaurant.name}`}
                  >
                    <Edit2 className="h-5 w-5" />
                  </button>
                </Can>
                <Can name={PERM.RESTAURANT_DELETE}>
                  <button
                    onClick={() => handleDelete(restaurant.id)}
                    className="text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    aria-label={`Delete ${restaurant.name}`}
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </Can>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {restaurant.address && (
                <div className="text-sm text-gray-600">
                  {restaurant.address}
                </div>
              )}
              {restaurant.phone && (
                <div className="text-sm text-gray-600">
                  {restaurant.phone}
                </div>
              )}
              {restaurant.email && (
                <div className="text-sm text-gray-600">
                  {restaurant.email}
                </div>
              )}
            </div>

            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-2 text-xs text-gray-500">
              <div>
                <span className="font-medium">Timezone:</span> {restaurant.timezone}
              </div>
              <div>
                <span className="font-medium">Currency:</span> {restaurant.currency}
              </div>
              <div>
                <span className="font-medium">Max Terminals:</span> {restaurant.maxTerminals ?? '—'}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <Link
                to={`/restaurants/${restaurant.id}/tables`}
                className="btn btn-outline h-9 text-sm"
              >
                Tables
              </Link>
              <Link
                to={`/restaurants/${restaurant.id}/terminals`}
                className="btn btn-outline h-9 text-sm"
              >
                Terminals
              </Link>
              <Link
                to={`/restaurants/${restaurant.id}/menu`}
                className="btn btn-outline h-9 text-sm"
              >
                Menu
              </Link>
              <Link
                to={`/restaurants/${restaurant.id}/booking`}
                className="btn btn-outline h-9 text-sm"
              >
                Booking
              </Link>
            </div>
          </div>
        ))}
      </div>

      {restaurants.length === 0 && (
        <div className="text-center py-12 card">
          <p className="text-gray-500">
            {hasActiveFilters ? 'No restaurants match your filters' : 'No restaurants found'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setStateFilter('all');
              }}
              className="btn btn-outline mt-4 h-10"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={data.first}
            className="btn btn-outline h-10 disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page + 1} of {data.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(data.totalPages - 1, p + 1))}
            disabled={data.last}
            className="btn btn-outline h-10 disabled:opacity-50"
          >
            Next
            <ChevronRight className="h-4 w-4 ml-1" />
          </button>
        </div>
      )}

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <RestaurantModal
          restaurant={editingRestaurant}
          onClose={closeModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['restaurants'] });
            closeModal();
          }}
        />
      )}
    </div>
  );
}
